import React, { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorState } from "@shared/common/components/State/ErrorState";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { ServiceTasksOverview } from "./ServiceTasksOverview";
import {
  PlanServiceTaskRequest,
  UnplanServiceTaskRequest,
} from "@opstel/gen/knop/ui/v1/api/plan_train_unit_details_pb";
import { TogglePlannedStateParameters } from "./ServiceTaskCell";
import { ServiceTask_ServiceTaskType } from "@opstel/gen/knop/ui/v1/model/servicetask_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { instances } from "@opstel/query/queryKeys/instanceKeys";
import { PlanTrainUnitDetails } from "@opstel/query/models/planTrainUnitDetails/planTrainUnitDetails";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";

/**
 * Provide Hip task overview with data.
 */
export function ServiceTasksOverviewWrapper(): React.JSX.Element {
  const { knopClient } = useKnopClient();
  const queryClient = useQueryClient();
  const { instanceId } = useCurrentInstanceId();
  const { data, error, isLoading } = useQuery({
    queryKey: instances.current(instanceId).planTrainUnitDetails,
    queryFn: () => knopClient.getPlanTrainUnitDetails(instanceId),
  });
  const toggleServiceTask = useMutation({
    mutationFn: ({ ptuGuid, serviceTaskType, newPlannedState }: TogglePlannedStateParameters) => {
      const protoParams: PlainMessage<PlanServiceTaskRequest> = {
        instanceId: instanceId!,
        planTrainUnitGuid: ptuGuid,
        serviceTaskType: ServiceTask_ServiceTaskType[serviceTaskType],
      };
      if (newPlannedState === "YES") {
        return knopClient.planServiceTask(new PlanServiceTaskRequest(protoParams));
      }
      return knopClient.unplanServiceTask(new UnplanServiceTaskRequest(protoParams));
    },
    onError: (err) => {
      console.error(err);
      messageToast({
        type: "error",
        message: "Service taak opslaan is mislukt. Neem contact op met de applicatiebeheerder.",
      });
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: instances.current(instanceId).planTrainUnitDetails,
      }),
  });

  const planTrainUnitDetails: PlanTrainUnitDetails[] = useMemo(() => {
    if (!data) return [];
    if (!toggleServiceTask.isPending || !toggleServiceTask.variables) return data;
    return data.map((details) => {
      if (details.ptuGuid !== toggleServiceTask.variables.ptuGuid) return details;
      return {
        ...details,
        serviceTasks: details.serviceTasks.map((task) => {
          if (task.type !== toggleServiceTask.variables.serviceTaskType) return task;
          return { ...task, isPlanned: toggleServiceTask.variables.newPlannedState };
        }),
      } satisfies PlanTrainUnitDetails;
    });
  }, [data, toggleServiceTask.isPending, toggleServiceTask.variables]);

  if (error) return <ErrorState />;
  return (
    <ServiceTasksOverview
      planTrainUnitDetails={planTrainUnitDetails}
      isLoading={isLoading}
      toggleServiceTask={toggleServiceTask.mutate}
    />
  );
}
